import { useEffect, useMemo, useState } from 'react';
import { Sparkles, X } from 'lucide-react';

import { apiClient } from '@/services/client';
import { cn } from '@/services/utils';

type CollocationItem = Awaited<ReturnType<typeof apiClient.getMeaningCollocations>>[number];

const PATTERN_PARTS: Record<string, [string, string]> = {
  ADJ_NOUN: ['adjective', 'noun'],
  VERB_OBJ: ['verb', 'noun'],
  ADV_ADJ: ['adverb', 'adjective'],
  ADV_VERB: ['adverb', 'verb'],
  VERB_PREP: ['verb', 'preposition'],
  ADJ_PREP: ['adjective', 'preposition'],
  PHRASAL_VERB: ['verb', 'particle'],
};

const PATTERN_ORDER = Object.keys(PATTERN_PARTS);
const COLLAPSED_GROUPS = 4;
const COLLAPSED_PARTNERS = 3;

type CollocationGroup = {
  key: string;
  label: (word: string) => string;
  wordFirst: boolean;
  wordPos: string;
  partnerPos: string;
  partners: string[];
};

const buildQuestion = (word: string, partner: string, group: CollocationGroup) => {
  const phrase = group.wordFirst ? `${word} ${partner}` : `${partner} ${word}`;
  return `Explain the collocation “${phrase}”. Here “${word}” is a ${group.wordPos} and “${partner}” is a ${group.partnerPos}, so take the parts of speech into account. Give its meaning, a translation, and a few short example sentences.`;
};

const buildGroups = (items: CollocationItem[]): CollocationGroup[] => {
  const groups = new Map<string, CollocationGroup & { order: number; score: number }>();
  for (const item of items) {
    const pattern = item.pattern ?? '';
    const partner = item.partner ?? '';
    const parts = PATTERN_PARTS[pattern];
    if (!parts || !partner) continue;
    const wordFirst = item.role === 'left';
    const key = `${pattern}:${item.role}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        wordFirst,
        wordPos: wordFirst ? parts[0] : parts[1],
        partnerPos: wordFirst ? parts[1] : parts[0],
        label: wordFirst ? (word) => `${word} + ${parts[1]}` : (word) => `${parts[0]} + ${word}`,
        partners: [],
        order: PATTERN_ORDER.indexOf(pattern) * 2 + (wordFirst ? 0 : 1),
        score: 0,
      };
      groups.set(key, group);
    }
    group.partners.push(partner);
    group.score = Math.max(group.score, item.logDice ?? 0);
  }
  return [...groups.values()].sort((a, b) => b.score - a.score || a.order - b.order);
};

export function CardCollocations({
  meaningId,
  word,
  onAsk,
}: {
  meaningId: string;
  word: string;
  onAsk: (question: string) => void;
}) {
  const [items, setItems] = useState<CollocationItem[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!selected) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setSelected(null);
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [selected]);

  useEffect(() => {
    const controller = new AbortController();
    setItems([]);
    setExpanded(false);
    setSelected(null);
    apiClient
      .getMeaningCollocations(meaningId, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setItems(result);
      })
      .catch(() => undefined);
    return () => controller.abort();
  }, [meaningId]);

  const groups = useMemo(() => buildGroups(items), [items]);
  if (groups.length === 0) return null;

  const total = groups.reduce((sum, group) => sum + group.partners.length, 0);
  const visibleGroups = expanded ? groups : groups.slice(0, COLLAPSED_GROUPS);
  const hasHidden =
    groups.length > COLLAPSED_GROUPS || groups.some((group) => group.partners.length > COLLAPSED_PARTNERS);

  return (
    <div className="mt-5 border-t border-slate-200 pt-5">
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a0472a]">Collocations</p>
        {hasHidden && (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="text-[13px] text-slate-500 transition hover:text-slate-900"
          >
            {expanded ? 'Show less' : `Show all ${total}`}
          </button>
        )}
      </div>
      <div className="mt-4 space-y-3">
        {visibleGroups.map((group) => (
          <div key={group.key} className="grid grid-cols-[9rem_1fr] items-start gap-x-4">
            <p className="pt-1.5 text-[13px] leading-5 text-slate-500">{group.label(word)}</p>
            <div>
              <div className="flex flex-wrap gap-2">
                {(expanded ? group.partners : group.partners.slice(0, COLLAPSED_PARTNERS)).map((partner) => {
                  const chipKey = `${group.key}|${partner}`;
                  const isSelected = selected === chipKey;
                  return (
                    <button
                      type="button"
                      key={partner}
                      aria-pressed={isSelected}
                      onClick={() => setSelected(isSelected ? null : chipKey)}
                      className={cn(
                        'inline-flex items-baseline gap-1 rounded-full border px-3 py-1 text-[14px] leading-5 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#496653] focus-visible:ring-offset-1',
                        isSelected ? 'border-[#496653] bg-[#e7f0e8]/60' : 'border-slate-200 hover:border-slate-300'
                      )}
                    >
                      {group.wordFirst && <span className="text-slate-400">{word}</span>}
                      <span className="font-semibold text-[#1e1c18]">{partner}</span>
                      {!group.wordFirst && <span className="text-slate-400">{word}</span>}
                    </button>
                  );
                })}
              </div>
              {selected?.startsWith(`${group.key}|`) && (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const partner = selected.slice(group.key.length + 1);
                      setSelected(null);
                      onAsk(buildQuestion(word, partner, group));
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#2f4938] px-3 py-1 text-[13px] font-medium text-white transition hover:bg-[#496653] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#496653] focus-visible:ring-offset-1"
                  >
                    <Sparkles className="size-3.5" aria-hidden="true" />
                    Explain with AI
                  </button>
                  <button
                    type="button"
                    aria-label="Cancel"
                    onClick={() => setSelected(null)}
                    className="rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  >
                    <X className="size-3.5" aria-hidden="true" />
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
